import {v4 as uuid} from 'uuid';
import {defer, pick, sortedUniq} from 'lodash';
import {
  BehaviorSubject,
  Observable,
  map,
  Subject,
  distinctUntilChanged,
} from 'rxjs';
import {
  ClientResponse,
  ConversationEvent,
  ConversationEventType,
  ConversationMessage,
  ConversationMessageStatus,
  ConversationParticipant,
  ConversationStatus,
  StreamTextResponse,
} from '../types';

export type MockResponse = {
  code: number;
  data?: unknown;
  error?: string;
};

type MockErrorResponse = MockResponse & {error: string};

export type MockServiceConversation = {
  id: string;
  status: ConversationStatus;
  messages: Record<string, ConversationMessage>;
  participants: Record<string, ConversationParticipant>;
};

export type StartMockConversationResponse = MockResponse &
  (MockErrorResponse | {data: MockServiceConversation});
export type GetMockConversationResponse = MockResponse &
  (MockErrorResponse | {data: MockServiceConversation});
export type GetMockConversationMessagesResponse = MockResponse &
  (MockErrorResponse | {data: Record<string, ConversationMessage>});
export type GetMockConversationParticipantsResponse = ClientResponse &
  (MockErrorResponse | {data: Record<string, ConversationParticipant>});

type MockServiceListener = (event: ConversationEvent) => void;

export class MockService {
  protected tokens: Set<string> = new Set();

  protected conversations$: BehaviorSubject<
    Record<string, MockServiceConversation>
  > = new BehaviorSubject({});

  protected events$: Subject<ConversationEvent> = new Subject();

  protected listeners: Set<MockServiceListener> = new Set();

  constructor() {
    this.eventsObservable().subscribe((event) => {
      this.listeners.forEach((listener) => listener(event));
    });
  }

  clearConversations(): void {
    this.conversations$.next({});
  }

  conversationIdsObservable(): Observable<string[]> {
    return this.conversations$.asObservable().pipe(
      distinctUntilChanged((a, b) => {
        return (
          sortedUniq(Object.keys(a)).join('-') ===
          sortedUniq(Object.keys(b)).join('-')
        );
      }),
      map(Object.keys),
    );
  }

  generateToken(): string {
    const token = uuid();
    this.tokens.add(token);
    return token;
  }

  startConversation(
    token: string | undefined | null,
    participant: ConversationParticipant,
  ): Promise<StartMockConversationResponse> {
    if (!token || !this.tokens.has(token)) {
      return this.notAuthed();
    }

    const conversation: MockServiceConversation = {
      id: uuid(),
      status: ConversationStatus.Active,
      messages: {},
      participants: {[participant.id]: {...participant}},
    };
    this._setConversation(conversation);

    this.emitNewEvent({
      name: 'conversation-updated',
      conversationId: conversation.id,
      status: conversation.status,
    });

    return Promise.resolve({
      code: 201,
      data: JSON.parse(JSON.stringify(conversation)),
    });
  }

  getConversation(
    token: string | undefined | null,
    id: string,
  ): Promise<GetMockConversationResponse> {
    if (!token || !this.tokens.has(token)) {
      return this.notAuthed();
    }

    const conversation = this._getConversation(id);
    if (!conversation) {
      return this.notFound();
    }

    return Promise.resolve({
      code: 200,
      data: JSON.parse(JSON.stringify(conversation)),
    });
  }

  joinConversation(
    token: string | undefined | null,
    id: string,
    participants: ConversationParticipant[],
  ): Promise<MockResponse> {
    if (!token || !this.tokens.has(token)) {
      return this.notAuthed();
    }

    const conversation = this._getConversation(id);
    if (!conversation) {
      return this.notFound();
    }

    participants.forEach((participant) => {
      conversation.participants[participant.id] = participant;

      this.emitNewEvent({
        name: 'conversation-participant-joined',
        conversationId: conversation.id,
        participant,
      });
    });

    this._setConversation(conversation);

    return this.success();
  }

  leaveConversation(
    token: string | undefined | null,
    id: string,
    participantIds: string[],
  ): Promise<MockResponse> {
    if (!token || !this.tokens.has(token)) {
      return this.notAuthed();
    }

    const conversation = this._getConversation(id);
    if (!conversation) {
      return this.notFound();
    }

    participantIds.forEach((participantId) => {
      const participant = conversation.participants[participantId];
      if (participant) {
        delete conversation.participants[participantId];

        this.emitNewEvent({
          name: 'conversation-participant-left',
          conversationId: conversation.id,
          participant,
        });
      }
    });

    // simulate conversation ending if participant left
    // and only 1 participant remains
    if (Object.keys(conversation.participants).length <= 1) {
      conversation.status = ConversationStatus.Inactive;
      this.emitNewEvent({
        name: 'conversation-updated',
        conversationId: conversation.id,
        status: conversation.status,
      });
    }

    this._setConversation(conversation);

    return this.success();
  }

  endConversation(
    token: string | undefined | null,
    id: string,
  ): Promise<MockResponse> {
    if (!token || !this.tokens.has(token)) {
      return this.notAuthed();
    }

    const conversation = this._getConversation(id);
    if (!conversation) {
      return this.notFound();
    }

    conversation.status = ConversationStatus.Inactive;

    const participantIds = Object.keys(conversation.participants);

    participantIds.forEach((participantId) => {
      const participant = conversation.participants[participantId];
      if (participant) {
        delete conversation.participants[participantId];

        this.emitNewEvent({
          name: 'conversation-participant-left',
          conversationId: conversation.id,
          participant,
        });
      }
    });

    this.emitNewEvent({
      name: 'conversation-updated',
      conversationId: conversation.id,
      status: conversation.status,
    });
    this._setConversation(conversation);

    return this.success();
  }

  getConversationParticipantIds(
    token: string | undefined | null,
    id: string,
  ): Promise<MockResponse> {
    if (!token || !this.tokens.has(token)) {
      return this.notAuthed();
    }

    const conversation = this._getConversation(id);
    if (!conversation) {
      return this.notFound();
    }

    return this.success(200, Object.keys(conversation.participants));
  }

  getConversationParticipants(
    token: string | undefined | null,
    id: string,
    participantIds?: string[],
  ): Promise<GetMockConversationParticipantsResponse> {
    if (!token || !this.tokens.has(token)) {
      return this.notAuthed();
    }

    const conversation = this._getConversation(id);
    if (!conversation) {
      return this.notFound();
    }

    const {participants} = conversation;
    const result = participantIds
      ? pick(participants, participantIds)
      : participants;

    return Promise.resolve({
      code: 200,
      data: JSON.parse(JSON.stringify(result)),
    });
  }

  getConversationMessages(
    token: string | undefined | null,
    id: string,
    messageIds?: string[] | number[],
  ): Promise<GetMockConversationMessagesResponse> {
    if (!token || !this.tokens.has(token)) {
      return this.notAuthed();
    }

    const conversation = this._getConversation(id);
    if (!conversation) {
      return this.notFound();
    }

    const {messages} = conversation;
    const result = messageIds ? pick(messages, messageIds) : messages;

    return Promise.resolve({
      code: 200,
      data: JSON.parse(JSON.stringify(result)),
    });
  }

  sendConversationMessage(
    token: string | undefined | null,
    id: string,
    message: ConversationMessage,
  ): Promise<MockResponse> {
    if (!token || !this.tokens.has(token)) {
      return this.notAuthed();
    }

    const conversation = this._getConversation(id);
    if (!conversation) {
      return this.notFound();
    }

    if (
      message.type === 'text-conversation-message' &&
      message.text.trim() === 'TEST_ERROR'
    ) {
      return Promise.resolve({code: 500, error: 'server error'});
    }

    conversation.messages[message.id] = {
      ...message,
      status: ConversationMessageStatus.Sent,
    };

    this.emitNewEvent({
      name: 'conversation-message-added',
      conversationId: conversation.id,
      participantId: message.participant.id,
      messageId: message.id,
    });

    this._setConversation(conversation);

    return this.success();
  }

  sendConversationTyping(
    token: string | undefined | null,
    id: string,
    participantId: string,
  ): Promise<MockResponse> {
    if (!token || !this.tokens.has(token)) {
      return this.notAuthed();
    }

    const conversation = this._getConversation(id);
    if (!conversation) {
      return this.notFound();
    }

    this.emitNewEvent({
      name: 'conversation-typing-started',
      conversationId: conversation.id,
      participantId,
    });

    return this.success();
  }

  streamText(streamId: string, _offset: number): Promise<StreamTextResponse> {
    const content =
      'This is some text that will be streamed. If you want to customize this, extend this class and implement your own method.';

    return Promise.resolve({
      code: 200,
      data: {
        type: 'success',
        llm_conversation_id: streamId,
        content,
        is_complete: true,
        was_halted: false,
        not_answerable: false,
        next_offset: content.length,
      },
    });
  }

  addListener(listener: (event: ConversationEvent) => void): void {
    this.listeners.add(listener);
  }

  removeListener(listener: (event: ConversationEvent) => void): void {
    this.listeners.delete(listener);
  }

  eventsObservable(): Observable<ConversationEvent> {
    return this.events$.asObservable();
  }

  protected emitNewEvent<T extends ConversationEventType>(event: T): void {
    this.emit(this.newEvent(event));
  }

  protected emit(event: ConversationEvent): void {
    this.emitEvent(event);
  }

  emitEvent = (event: ConversationEvent): void => {
    defer(() => this.events$.next(event));
  };

  private _getConversation(id: string): MockServiceConversation | null {
    const conversations = this.conversations$.getValue();
    return conversations[id] || null;
  }

  private _setConversation(conversation: MockServiceConversation): void {
    const conversations = {...this.conversations$.getValue()};
    conversations[conversation.id] = conversation;
    this.conversations$.next(conversations);
  }

  private newEvent = <T extends ConversationEventType>(
    event: T,
  ): ConversationEvent => {
    return {
      id: uuid(),
      timestamp: Date.now(),
      ...event,
    };
  };

  // eslint-disable-next-line default-param-last
  private success = (code = 200, data?: unknown): Promise<MockResponse> => {
    return Promise.resolve(data ? {code, data} : {code});
  };

  private notAuthed = (
    error = 'not authorized',
  ): Promise<MockErrorResponse> => {
    return Promise.resolve({code: 401, error});
  };

  private notFound = (error = 'not found'): Promise<MockErrorResponse> => {
    return Promise.resolve({code: 404, error});
  };
}

export const SharedMockService = new MockService();
